import { useTranslation } from '@/i18n';
import type { ButtonV2Props } from '@moneyforward/ap-frontend-components';
import { IconTrailing } from '@moneyforward/ap-frontend-components';
import { useCallback, useMemo } from 'react';
import {
  AgentEntryOpenDetail,
  useOpenAgentEntryModal,
} from '../../hooks/useOpenAgentEntryModal';
import { useCheckProxyApplicantPermission } from '../../hooks';
import type {
  ProxyOnButtonGroupProps,
  UseProxyOnButtonGroupItemsParams,
} from '../types';
import {
  createLinkAndProxyMenuItems,
  createMyApplicationProxyMenuItems,
} from '../utils';

/** Shared button props for Import/AI OCR/Create new tertiary dropdown triggers. */
const tertiaryDropdownProps = (
  children: string,
  isDisabled?: boolean
): ButtonV2Props => ({
  children,
  size: 'sm',
  isTertiary: true,
  rightIcon: <IconTrailing size={16} />,
  isDisabled,
});

/**
 * Builds all menu items and button props for ProxyOnButtonGroup.
 * Call from parent (InvoiceReportsButtonGroup) so link/callback control stays outside.
 * Returns: export props (passed through) + import/aiOcr/createNew menu items and button props.
 */
export function useProxyOnButtonGroupItems(
  params: UseProxyOnButtonGroupItemsParams
): ProxyOnButtonGroupProps {
  const { t } = useTranslation();
  const openAgentEntryModal = useOpenAgentEntryModal();
  const checkProxyPermission = useCheckProxyApplicantPermission();
  const {
    reportTypeId,
    aiOcrHref,
    createNewHref,
    onImport,
    onImportProxyApplication,
    isFetching = false,
    noPayeeTooltip,
  } = params;

  const onAgentEntryRegister = useCallback(async () => {
    const allowed = await checkProxyPermission(
      t('invoice_reports.proxy_applicant.restricted_error')
    );
    if (!allowed) return;
    openAgentEntryModal(AgentEntryOpenDetail.InvoiceReport);
  }, [openAgentEntryModal, checkProxyPermission, t]);

  const onAgentEntryBulkUpload = useCallback(async () => {
    const allowed = await checkProxyPermission(
      t('invoice_reports.proxy_applicant.restricted_error')
    );
    if (!allowed) return;
    openAgentEntryModal(AgentEntryOpenDetail.InvoiceReportBulkUpload);
  }, [openAgentEntryModal, checkProxyPermission, t]);

  const aiOcrBulkItems = useMemo(
    () =>
      createLinkAndProxyMenuItems({
        t,
        href: aiOcrHref,
        disabled: !reportTypeId,
        onProxyClick: onAgentEntryBulkUpload,
        myApplicationKey: 'ai_ocr_bulk',
        proxyKey: 'proxy_application',
      }),
    [t, reportTypeId, aiOcrHref, onAgentEntryBulkUpload]
  );

  const createNewItems = useMemo(
    () =>
      createLinkAndProxyMenuItems({
        t,
        href: createNewHref,
        disabled: !reportTypeId,
        onProxyClick: onAgentEntryRegister,
        myApplicationKey: 'create_new',
        proxyKey: 'register_my_application',
      }),
    [t, reportTypeId, createNewHref, onAgentEntryRegister]
  );

  const aiOcrButtonProps: ButtonV2Props = useMemo(
    () => ({
      children: t('invoice_reports.button_groups.ai_ocr_bulk_draft'),
      size: 'sm',
      isTertiary: false,
      rightIcon: <IconTrailing size={16} />,
      isDisabled: !!noPayeeTooltip || isFetching,
    }),
    [t, noPayeeTooltip, isFetching]
  );

  const createNewButtonProps: ButtonV2Props = useMemo(
    () => ({
      children: t('invoice_reports.button_groups.create_new'),
      size: 'sm',
      color: 'primary',
      isSecondary: false,
      isTertiary: false,
      rightIcon: <IconTrailing size={16} />,
      isDisabled: !!noPayeeTooltip || isFetching,
    }),
    [t, noPayeeTooltip, isFetching]
  );

  const importMenuItems = useMemo(
    () =>
      createMyApplicationProxyMenuItems({
        t,
        onMyApplication: onImport,
        onProxyApplication: onImportProxyApplication,
      }),
    [t, onImport, onImportProxyApplication]
  );

  const importButtonProps = useMemo(
    () =>
      tertiaryDropdownProps(
        t('invoice_reports.button_groups.import'),
        !!noPayeeTooltip || isFetching
      ),
    [t, noPayeeTooltip, isFetching]
  );

  return {
    importMenuItems,
    importButtonProps,
    aiOcrBulkItems,
    aiOcrButtonProps,
    createNewItems,
    createNewButtonProps,
    noPayeeTooltip,
  };
}
